import React, { useEffect, useState } from "react";
import { Button, Typography, Grid } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { useStepData } from "../context";
import { uploadValidator, organizeValidator, matchValidator, errorsValidator, type ValidatorType } from "../steps/validation";
import { availableSteps } from "..";

type Validator = ValidatorType;
type Validators = Record<string, Validator>;

const validators: Validators = {
  "Upload": uploadValidator,
  "Organize": organizeValidator,
  "Match": matchValidator,
  "Errors": errorsValidator,
};

const errorMessages = {
  "Match": "You must map at least 3 columns to import applications.",
  "Errors": "Fix the errors in your spreadsheet, then re-upload it.",
};

// Map a URL path segment to its step name. The URL is the source of truth for
// the current step; relying on the `stepName` context value is racy because it
// is set asynchronously via useEffect inside each step component, so it can lag
// behind the route immediately after navigation.
const segmentToStepName: Record<string, string> = {
  upload: "Upload",
  organize: "Organize",
  match: "Match",
  validate: "Validate",
  errors: "Errors",
  finalize: "Finalize",
  success: "Confirmation",
};

const StepNavigation: React.FC = () => {
  const { stepData, stepName } = useStepData();
  const [isValid, setIsValid] = useState(false);
  const location = useLocation();
  const currentSegment = location.pathname.split("/").filter(Boolean).pop() || "";
  const currentStepName = segmentToStepName[currentSegment] ?? stepName;
  const currentStepIndex = availableSteps.findIndex((step) => currentStepName === step);
  const notLastStep = currentStepIndex < availableSteps.length - 1;
  const navigate = useNavigate();
  const [continueVisible, setContinueVisible] = useState(true);

  useEffect(() => {
    if (validators[currentStepName]) {
      setIsValid(validators[currentStepName](stepData));
    } else {
      setIsValid(true);
    }
  }, [stepData, currentStepName]);

  const handleContinue = () => {
    if (currentStepIndex < availableSteps.length - 1) {
      navigate(`/dash/manager/settings/imports/${(availableSteps[currentStepIndex + 1] ?? "Unknown").toLowerCase()}`);
    }
  };

  useEffect(() => {
    if (currentStepName === "Upload") {
      // eslint-disable-next-line new-cap
      setContinueVisible(validators.Upload && validators.Upload(stepData));
    } else {
      setContinueVisible(true);
    }
  }, [currentStepName, stepData]);

  if (!continueVisible || ["Finalize", "Confirmation"].includes(currentStepName)) {
    return null;
  }
  return (
    <Grid size={{ xs: 12 }}>
      {!isValid && (errorMessages[currentStepName as keyof typeof errorMessages]) && (
        <Typography variant="body1" color="error">{errorMessages[currentStepName as keyof typeof errorMessages]}</Typography>)}
      <Button
        data-cy="continue-button"
        variant="contained"
        color="primary"
        onClick={handleContinue}
        disabled={notLastStep && !isValid}
      >
        Continue
      </Button>
    </Grid>
  );
};

export default StepNavigation;
